import React, { useState, useEffect } from 'react';
import { Upload, X, Send } from 'lucide-react';
import { supabase } from '../lib/supabase';

export function FeatureRequest() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [screenshots, setScreenshots] = useState<string[]>([]);
  const [examples, setExamples] = useState<string[]>([]);
  const [newExample, setNewExample] = useState('');
  const [userRole, setUserRole] = useState<'admin' | 'employee' | 'customer'>('customer');

  // Get user role on component mount
  useEffect(() => {
    async function getUserRole() {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .single();
        
        if (data?.role) {
          setUserRole(data.role);
        }
      }
    }
    getUserRole();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { error: submitError } = await supabase
        .from('feature_requests')
        .insert([
          {
            user_role: userRole,
            feedback,
            screenshots,
            examples,
            user_id: user?.id
          }
        ]);

      if (submitError) throw submitError;

      setSuccess(true);
      setFeedback('');
      setScreenshots([]);
      setExamples([]);
      setNewExample('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleScreenshotUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    const file = e.target.files[0];
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;

    try {
      const { error: uploadError, data } = await supabase.storage
        .from('feedback-uploads')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      if (data) {
        setScreenshots([...screenshots, fileName]);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const addExample = () => {
    if (newExample && !examples.includes(newExample)) {
      setExamples([...examples, newExample]);
      setNewExample('');
    }
  };

  const removeExample = (index: number) => {
    setExamples(examples.filter((_, i) => i !== index));
  };

  const removeScreenshot = async (index: number) => {
    const fileName = screenshots[index];
    try {
      const { error: deleteError } = await supabase.storage
        .from('feedback-uploads')
        .remove([fileName]);

      if (deleteError) throw deleteError;

      setScreenshots(screenshots.filter((_, i) => i !== index));
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-xl p-8">
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#5e9bb8] to-[#499da8] mb-8">
            Request a Feature
          </h1>

          {success ? (
            <div className="bg-[#5e9bb8]/10 dark:bg-[#5e9bb8]/20 border border-[#5e9bb8]/20 dark:border-[#5e9bb8]/30 rounded-xl p-6 text-center">
              <h2 className="text-xl font-semibold text-[#5e9bb8] dark:text-[#5e9bb8] mb-2">
                Thank you for your request!
              </h2>
              <p className="text-[#499da8] dark:text-[#499da8]/90">
                We've received your feature request and will review it shortly.
              </p>
              <button
                onClick={() => setSuccess(false)}
                className="mt-4 px-6 py-2 bg-gradient-to-r from-[#5e9bb8] to-[#499da8] hover:from-[#4d8299] hover:to-[#3d8289] text-white rounded-xl transform hover:-translate-y-0.5 transition-all duration-300"
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Feedback *
                </label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  required
                  rows={6}
                  placeholder="Please describe the feature you'd like to request..."
                  className="w-full rounded-xl border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#499da8] focus:ring-[#499da8] dark:bg-gray-700/50 dark:text-white transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Screenshots
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-xl">
                  <div className="space-y-1 text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600 dark:text-gray-400">
                      <label className="relative cursor-pointer bg-white dark:bg-gray-700 rounded-md font-medium text-[#499da8] hover:text-[#5e9bb8] focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#499da8]">
                        <span>Upload files</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={handleScreenshotUpload}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                </div>

                {screenshots.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {screenshots.map((screenshot, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {screenshot}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeScreenshot(index)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Example Links
                </label>
                <div className="flex gap-2">
                  <input
                    type="url"
                    value={newExample}
                    onChange={(e) => setNewExample(e.target.value)}
                    placeholder="https://example.com"
                    className="flex-1 rounded-xl border-gray-300 dark:border-gray-600 shadow-sm focus:border-[#499da8] focus:ring-[#499da8] dark:bg-gray-700/50 dark:text-white transition-all duration-300"
                  />
                  <button
                    type="button"
                    onClick={addExample}
                    className="px-4 py-2 bg-[#499da8] text-white rounded-xl hover:bg-[#5e9bb8] transition-colors duration-300"
                  >
                    Add
                  </button>
                </div>

                {examples.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {examples.map((example, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <a
                          href={example}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[#499da8] hover:text-[#5e9bb8] dark:text-[#5e9bb8] dark:hover:text-[#499da8]"
                        >
                          {example}
                        </a>
                        <button
                          type="button"
                          onClick={() => removeExample(index)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                  <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-xl text-base font-medium text-white bg-gradient-to-r from-[#5e9bb8] to-[#499da8] hover:from-[#4d8299] hover:to-[#3d8289] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#499da8] transform hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Submit Request
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}